import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/useAuth';
import {
  Lock,
  Mail,
  User,
  Building,
  CarFront,
  Eye,
  EyeOff,
  CheckCircle,
  Clock,
} from 'lucide-react';
import type { UserFormData, OperatorType } from '../types/user';
import { PhoneInput } from '../../../shared/components/forms/PhoneInput';
import { GoogleOAuthButton } from '../components/GoogleOAuthButton';
import { getFieldErrors, getErrorMessage } from '../../../shared/utils/errorUtils';

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { signUp, redirectToLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const hasSetDefaults = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [countdown, setCountdown] = useState(5);

  // Get operatorType from URL parameters
  const urlOperatorType = searchParams.get('operatorType');

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    userType: urlOperatorType ? 'operator' : 'traveler',
    operatorType: (urlOperatorType as OperatorType) || undefined,
    companyName: '',
    phoneNumber: '',
  });

  // Set default query parameters on load if none exist
  useEffect(() => {
    if (!hasSetDefaults.current) {
      const type = searchParams.get('type');
      const operatorType = searchParams.get('operatorType');

      // If no query parameters exist, set default to traveler
      if (!type && !operatorType) {
        const defaultParams = new URLSearchParams();
        defaultParams.set('type', 'traveler');
        setSearchParams(defaultParams);
      }
      hasSetDefaults.current = true;
    }
  }, [searchParams, setSearchParams]);

  // Countdown effect for auto-redirect
  useEffect(() => {
    if (registrationSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (registrationSuccess && countdown === 0) {
      navigate('/');
    }
  }, [registrationSuccess, countdown, navigate]);

  // Update form data when URL parameters change
  useEffect(() => {
    const location = searchParams.get('location');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const guests = searchParams.get('guests');

    if (location || startDate || endDate || guests) {
      setFormData(prev => ({
        ...prev,
      }));
    }
  }, [searchParams, setSearchParams]);

  // Helper function to get email service URL
  const getEmailServiceUrl = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase();
    const emailServices: { [key: string]: string } = {
      'gmail.com': 'https://mail.google.com',
      'yahoo.com': 'https://mail.yahoo.com',
      'outlook.com': 'https://outlook.live.com',
      'hotmail.com': 'https://outlook.live.com',
      'icloud.com': 'https://www.icloud.com/mail',
      'aol.com': 'https://mail.aol.com',
      'protonmail.com': 'https://mail.proton.me',
      'zoho.com': 'https://mail.zoho.com',
    };
    return emailServices[domain] || `https://${domain}`;
  };

  // Update URL query parameters when form data changes
  const updateURLParams = (newFormData: UserFormData) => {
    const newParams = new URLSearchParams();
    newParams.set('type', newFormData.userType);
    if (newFormData.operatorType) {
      newParams.set('operatorType', newFormData.operatorType);
    }
    setSearchParams(newParams);
  };

  // Update form data and URL params
  const updateFormData = (newData: Partial<UserFormData>) => {
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);

    // Only update URL params when userType or operatorType changes
    if (newData.userType !== undefined || newData.operatorType !== undefined) {
      updateURLParams(updatedData);
    }

    // Debug email field changes
    if (newData.email !== undefined) {
      console.log('Email field updated:', newData.email);
      console.log('Current formData.email:', updatedData.email);
    }
  };

  // Helper function to get field error
  const getFieldError = (fieldName: string): string | undefined => {
    return errors[fieldName]?.[0];
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName: string): boolean => {
    return !!errors[fieldName];
  };

  // Client-side validation
  const validateForm = (): {
    isValid: boolean;
    clientErrors: { [key: string]: string[] };
  } => {
    const clientErrors: { [key: string]: string[] } = {};

    // Required field validation
    if (!formData.first_name.trim()) {
      clientErrors.first_name = ['First name is required'];
    }
    if (!formData.last_name.trim()) {
      clientErrors.last_name = ['Last name is required'];
    }
    if (!formData.email.trim()) {
      clientErrors.email = ['Email is required'];
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      clientErrors.email = ['Please enter a valid email address'];
    }
    if (!formData.password) {
      clientErrors.password = ['Password is required'];
    } else if (formData.password.length < 8) {
      clientErrors.password = ['Password must be at least 8 characters long'];
    }
    if (!formData.confirmPassword) {
      clientErrors.password2 = ['Please confirm your password'];
    } else if (formData.password !== formData.confirmPassword) {
      clientErrors.password2 = ['Passwords do not match'];
    }

    // Operator-specific validation
    if (formData.userType === 'operator') {
      if (!formData.companyName?.trim()) {
        clientErrors.company_name = ['Company name is required for operators'];
      }
      if (!formData.phoneNumber?.trim()) {
        clientErrors.phone_number = ['Phone number is required for operators'];
      }
      if (!formData.operatorType) {
        clientErrors.operator_type = ['Please select an operator type'];
      }
    }

    return { isValid: Object.keys(clientErrors).length === 0, clientErrors };
  };

  const operatorTypes = [
    {
      id: 'tour_operator' as OperatorType,
      label: 'Tour Operator',
      description: 'Full-service safari company with oepration license',
      icon: Building,
    },
    // Travel Agent option hidden for now but kept in code
    // {
    //   id: 'travel_agent' as OperatorType,
    //   label: 'Travel Agent',
    //   description: 'Book safaris for clients without vehicles',
    //   icon: Briefcase,
    //   tooltip: 'Coming soon',
    //   tooltipPosition: 'top',
    //   tooltipColor: 'bg-red-500',
    //   cursor: 'not-allowed',
    // },
    {
      id: 'transporter' as OperatorType,
      label: 'Transporter',
      description: 'Vehicle rental and transport services',
      icon: CarFront,
    },
  ];

  const descriptions = {
    traveler: {
      title: 'Welcome, Explorer!',
      description:
        "Book and experience amazing safaris across Tanzania's most beautiful destinations.",
      image:
        'https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg',
    },
    operator: {
      tour_operator: {
        title: 'Welcome, Safari Company!',
        description:
          'Manage your fleet, create safari packages, and grow your business across Tanzania.',
        image:
          'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg',
      },
      transporter: {
        title: 'Welcome, Vehicle Operator!',
        description:
          'List your vehicles and connect with tour operators and travelers.',
        image:
          'https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg',
      },
      travel_agent: {
        title: 'Welcome, Travel Agent!',
        description: 'Connect travelers with the perfect safari experiences.',
        image:
          'https://images.pexels.com/photos/5669788/pexels-photo-5669788.jpeg',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({}); // Clear previous errors

    // Debug current form data
    console.log('Current formData before validation:', formData);

    // Client-side validation
    const { isValid, clientErrors } = validateForm();
    if (!isValid) {
      setErrors(clientErrors);
      setLoading(false);
      return;
    }

    try {
      // Create backend-compatible data structure
      const backendData = {
        email: formData.email,
        password: formData.password,
        password2: formData.confirmPassword,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phoneNumber,
        user_type: formData.userType,
        ...(formData.userType === 'operator' && {
          company_name: formData.companyName,
          operator_type: formData.operatorType,
        }),
      };

      console.log('Sending to backend:', backendData);

      // Send the form data (no query parameters needed - user_type is in body)
      await signUp(backendData as unknown as UserFormData);
      setRegistrationSuccess(true);
      setRegisteredEmail(formData.email);
      setCountdown(5);
    } catch (error: unknown) {
      // Try to extract field-specific errors from the API response
      const fieldErrors = getFieldErrors(error);
      
      if (fieldErrors) {
        // Set field-specific errors for inline display
        setErrors(fieldErrors);
        
        // Show a toast with a summary if there are non-field errors
        if (fieldErrors.non_field_errors) {
          toast.error(fieldErrors.non_field_errors.join('. '));
        } else {
          // Show generic validation error toast
          const errorCount = Object.keys(fieldErrors).length;
          toast.error(`Please fix ${errorCount} error${errorCount > 1 ? 's' : ''} in the form.`);
        }
      } else {
        // Fallback to generic error message
        const errorMessage = getErrorMessage(error, 'Registration failed. Please try again.');
        toast.error(errorMessage);
        setErrors({ non_field_errors: [errorMessage] });
      }
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDescription = () => {
    if (formData.userType === 'traveler') {
      return descriptions.traveler;
    }
    if (formData.operatorType) {
      return descriptions.operator[formData.operatorType];
    }
    return {
      title: 'Welcome, Tour Operator!',
      description: 'Choose your operation type to get started.',
      image:
        'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg',
    };
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50 py-8 px-4 sm:px-6 mt-20">
      <div className="w-full max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form Section */}
          <div className="flex flex-col items-center justify-center order-2 lg:order-1">
            <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 w-full max-w-md mx-auto">
              {registrationSuccess ? (
                // Success State
                <div className="text-center">
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>

                  <h1 className="text-xl font-bold mb-2 text-gray-900">
                    Registration Successful!
                  </h1>
                  <p className="text-gray-600 mb-6">
                    Welcome to Dala Rides! Please verify your email to complete
                    your account setup.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-blue-900 mb-2">
                      Email Verification Required
                    </h3>
                    <p className="text-sm text-blue-700 mb-3">
                      We've sent a verification link to your email{' '}
                      <span className="font-medium">{registeredEmail}</span>
                    </p>
                    {/* Verify button with link to email service with icon */}
                    <a
                      href={getEmailServiceUrl(registeredEmail)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Mail className="w-4 h-4" />
                      Verify Email
                    </a>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{countdown} seconds</span>
                  </div>
                </div>
              ) : (
                // Registration Form
                <>
                  <div className="text-center mb-6">
                    <h1 className="text-2xl sm:text-xl font-bold mb-2">Create an Account</h1>
                    <p className="text-sm text-gray-600">
                      Join our community of safari enthusiasts
                    </p>
                  </div>

                  {/* Non-field errors display */}
                  {errors.non_field_errors &&
                    errors.non_field_errors.length > 0 && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            <svg
                              className="w-4 h-4 text-red-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="text-sm text-red-700">
                            {errors.non_field_errors.map((error, index) => (
                              <p key={index} className="mb-1 last:mb-0">
                                {error}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* User Type Toggle */}
                    <div className="flex bg-primary-50 border gap-1 border-primary-200 rounded-lg p-1 w-fit mx-auto shadow-sm">
                      <button
                        type="button"
                        onClick={() =>
                          updateFormData({
                            userType: 'traveler',
                            operatorType: undefined,
                          })
                        }
                        className={`px-4 py-1.5 sm:py-1 rounded-lg text-sm font-semibold transition-all duration-200 focus:outline-none ${
                          formData.userType === 'traveler'
                            ? 'bg-primary-500 text-white shadow'
                            : 'bg-transparent text-primary-600 hover:bg-primary-100'
                        }`}
                      >
                        Traveler
                      </button>
                      <button
                        type="button"
                        onClick={() => updateFormData({ 
                          userType: 'operator',
                          operatorType: 'tour_operator'
                        })}
                        className={`px-4 py-1.5 sm:py-1 rounded-lg text-sm h-fit font-semibold transition-all duration-200 focus:outline-none ${
                          formData.userType === 'operator'
                            ? 'bg-primary-500 text-white shadow'
                            : 'bg-transparent text-primary-600 hover:bg-primary-100'
                        }`}
                      >
                        Tour Operator
                      </button>
                    </div>

                    {/* Operator Type Selection */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        formData.userType === 'operator'
                          ? 'max-h-20 opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="flex flex-wrap justify-center bg-teal-50 border border-teal-200 rounded-xl p-2 gap-2 shadow-sm w-fit mx-auto">
                        {operatorTypes.map(type => {
                          const Icon = type.icon;
                          const isSelected = formData.operatorType === type.id;
                          const isTravelAgent = type.id === 'travel_agent';

                          return (
                            <div key={type.id} className="relative group">
                              <button
                                type="button"
                                onClick={() =>
                                  !isTravelAgent &&
                                  updateFormData({ operatorType: type.id })
                                }
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 focus:outline-none flex items-center gap-1.5 ${
                                  isSelected
                                    ? 'bg-teal-500 text-white shadow'
                                    : isTravelAgent
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-transparent text-teal-600 hover:bg-teal-100'
                                }`}
                                disabled={isTravelAgent}
                              >
                                <Icon
                                  className={`w-3 h-3 flex-shrink-0 transition-transform duration-300 ${
                                    isSelected ? 'scale-110' : 'scale-100'
                                  }`}
                                />
                                <span className="whitespace-nowrap">
                                  {type.label}
                                </span>
                              </button>

                              {/* Tooltip for Travel Agent */}
                              {isTravelAgent && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                  Coming soon
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-500"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Name Fields - Stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={e =>
                              updateFormData({ first_name: e.target.value })
                            }
                            className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm ${
                              hasFieldError('first_name')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Enter first name"
                            required
                          />
                        </div>
                        {getFieldError('first_name') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('first_name')}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={e =>
                              updateFormData({ last_name: e.target.value })
                            }
                            className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm ${
                              hasFieldError('last_name')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Enter last name"
                            required
                          />
                        </div>
                        {getFieldError('last_name') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('last_name')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Company Name - Full Width - Only for Operators */}
                    {formData.userType === 'operator' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.companyName}
                            onChange={e =>
                              updateFormData({ companyName: e.target.value })
                            }
                            className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm ${
                              hasFieldError('company_name')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Enter company name"
                            required={formData.userType === 'operator'}
                          />
                        </div>
                        {getFieldError('company_name') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('company_name')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Email and Phone - Stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={e =>
                              updateFormData({ email: e.target.value })
                            }
                            className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm ${
                              hasFieldError('email')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                        {getFieldError('email') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('email')}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <PhoneInput
                          value={formData.phoneNumber || ''}
                          onChange={value =>
                            updateFormData({ phoneNumber: value })
                          }
                          error={getFieldError('phone_number')}
                          placeholder="Phone number"
                          required={formData.userType === 'operator'}
                        />
                      </div>
                    </div>

                    {/* Password Fields - Stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={e =>
                              updateFormData({ password: e.target.value })
                            }
                            className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm outline-none ${
                              hasFieldError('password')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Create password"
                            required
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {getFieldError('password') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('password')}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="password2"
                            value={formData.confirmPassword || ''}
                            onChange={e =>
                              updateFormData({ confirmPassword: e.target.value })
                            }
                            className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm outline-none focus:outline-none ${
                              hasFieldError('password2')
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200'
                            }`}
                            placeholder="Confirm password"
                            required
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {getFieldError('password2') && (
                          <p className="text-red-500 text-xs mt-1">
                            {getFieldError('password2')}
                          </p>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={
                        loading ||
                        (formData.userType === 'operator' &&
                          !formData.operatorType)
                      }
                      className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-all duration-300 disabled:bg-blue-400 transform hover:scale-[1.02] active:scale-[0.98] text-sm font-medium"
                    >
                      {loading ? 'Creating account...' : 'Create Account'}
                    </button>

                    {/* Social Login Options with Smooth Transitions */}
                    <div
                      className={`transition-all duration-300 ease-in-out ${
                        formData.userType === 'traveler'
                          ? 'max-h-32 opacity-100'
                          : 'max-h-0 opacity-0 overflow-hidden'
                      }`}
                    >
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-4 bg-white text-gray-500">
                            Or continue with
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <button
                          type="button"
                          className="flex items-center justify-center gap-2 px-3 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-sm w-full max-w-xs"
                        >
                          <GoogleOAuthButton />
                        </button>
                      </div>
                    </div>

                    <p className="text-center text-sm text-gray-600">
                      Already have an account?{' '}
                      <button
                        onClick={() => redirectToLogin('', '/register')}
                        className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300 cursor-pointer"
                      >
                        Sign in
                      </button>
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* Right side - Dynamic Description with Image Transition */}
          <div className="hidden lg:block order-1 lg:order-2">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden h-[700px]">
              <div className="relative h-full transition-all duration-700 ease-in-out">
                <img
                  src={getCurrentDescription().image}
                  alt="Background"
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out transform hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/30">
                  <div className="relative h-full flex flex-col justify-center p-11">
                    <h2 className="text-3xl font-bold text-white mb-3 transition-all duration-300 ease-in-out">
                      {getCurrentDescription().title}
                    </h2>
                    <p className="text-lg text-white/90 max-w-lg transition-all duration-300 ease-in-out">
                      {getCurrentDescription().description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
